import { GraduationCap, Clock, Landmark } from 'lucide-react';
import { Reveal, Eyebrow, SectionTitle, Glass, Disclaimer, SectionShell, CountUp, PINK, PURPLE, GOLD } from './shared';

const TRAINING_AREAS = [
  'Musical Theatre', 'Acting & Screen Performance', 'Singing & Vocal Technique', 'Dance & Movement',
  'Audition Preparation', 'Live Productions', 'Creative & Digital Performance',
];

const PROGRESSION = ['Drama Schools', 'Conservatoires', 'Universities', 'Professional Performing Arts Training', 'Creative Industries Employment'];

export const Qualification = () => (
  <SectionShell id="qualification" data-testid="college-qualification">
    <Reveal>
      <Eyebrow color={PURPLE}>Education With a Clear Future</Eyebrow>
      <SectionTitle>More Than Training. A Recognised Qualification.</SectionTitle>
      <p className="text-white/65 max-w-3xl leading-relaxed">
        The UPA Creative Hub is being developed to offer a specialist post-16 performing arts education programme
        for young people aged <span className="text-white font-semibold">16–19</span>. Our ambition is to combine
        exceptional professional training with a nationally recognised Level 3 qualification — giving students the
        skills, experience and credentials to progress into higher education and the creative industries.
      </p>
    </Reveal>

    <div className="grid lg:grid-cols-5 gap-8 mt-12 items-stretch">
      <Reveal className="lg:col-span-3">
        <Glass className="h-full border-[#7000FF]/40" data-testid="college-qualification-pathway">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-11 h-11 rounded-full flex items-center justify-center border border-[#7000FF]/50"
              style={{ background: 'rgba(112,0,255,0.15)' }}>
              <GraduationCap className="w-5 h-5" style={{ color: '#b07aff' }} />
            </span>
            <h3 className="font-heading text-xl text-white">Proposed Qualification Pathway</h3>
          </div>
          <p className="text-white/75 leading-relaxed">
            A two-year <span className="text-white font-semibold">Level 3 Extended Diploma in Performing Arts</span>,
            with <span className="text-white font-semibold">UAL Awarding Body</span> and{' '}
            <span className="text-white font-semibold">RSL Awards</span> being explored as potential awarding organisations.
          </p>
          <div className="mt-7 flex items-end gap-4">
            <p className="font-heading text-5xl sm:text-6xl leading-none">
              <CountUp value={168} className="college-shimmer" />
            </p>
            <p className="text-white/55 text-sm leading-snug pb-1">
              potential UCAS tariff points,<br />depending on qualification and final grade
            </p>
          </div>
          <div className="mt-7 pt-6 border-t border-white/10">
            <p className="text-xs uppercase tracking-widest mb-2" style={{ color: GOLD }}>Student outcomes</p>
            <p className="text-white/65 text-sm leading-relaxed">
              Students would work towards a recognised qualification alongside professional performance experience,
              audition material, portfolio development and industry knowledge.
            </p>
          </div>
        </Glass>
      </Reveal>

      <Reveal delay={120} className="lg:col-span-2">
        <Glass className="h-full">
          <h3 className="font-heading text-xl text-white mb-5">Specialist Training Areas</h3>
          <div className="flex flex-wrap gap-2.5">
            {TRAINING_AREAS.map((t, i) => (
              <span key={t} className="px-4 py-2 rounded-full text-xs sm:text-sm text-white/85 border transition-colors hover:text-white"
                style={{ borderColor: `${[PINK, PURPLE, GOLD][i % 3]}55`, background: `${[PINK, PURPLE, GOLD][i % 3]}10` }}>
                {t}
              </span>
            ))}
          </div>
        </Glass>
      </Reveal>
    </div>

    <Reveal>
      <div className="mt-10 rounded-2xl border border-white/10 p-6 sm:p-8"
        style={{ background: 'linear-gradient(90deg, rgba(112,0,255,0.12), rgba(255,0,127,0.08))' }}
        data-testid="college-progression">
        <p className="text-xs uppercase tracking-[0.3em] mb-4 font-semibold" style={{ color: PINK }}>Progression Opportunities</p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          {PROGRESSION.map((p, i) => (
            <span key={p} className="flex items-center gap-3 text-white font-heading text-sm sm:text-base">
              {p}
              {i < PROGRESSION.length - 1 && <span className="text-white/25" aria-hidden="true">|</span>}
            </span>
          ))}
        </div>
      </div>
      <Disclaimer>Qualification accreditation and programme delivery remain subject to the necessary approvals.</Disclaimer>
    </Reveal>
  </SectionShell>
);

const MODEL_BLOCKS = [
  { when: 'Monday–Friday', what: 'Specialist Education', color: PURPLE,
    body: 'A proposed full-time post-16 performing arts programme, combining vocational education, industry-led training, rehearsals, assessment and productions.' },
  { when: 'Evenings & Weekends', what: 'Universal Performing Arts', color: PINK,
    body: 'UPA\u2019s established commercial theatre school and community training programmes, including acting, singing, dance, adult classes and specialist workshops.' },
  { when: 'School Holidays', what: 'Creative Experiences', color: GOLD,
    body: 'Performing arts bootcamps, masterclasses, intensive training, productions and community creative opportunities.' },
];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const Cell = ({ color, label }) => (
  <div className="rounded-lg py-2.5 px-1 text-center text-[10px] sm:text-xs font-semibold leading-tight border"
    style={{ background: `${color}1c`, borderColor: `${color}55`, color: color === GOLD ? '#ffe066' : color === PURPLE ? '#c49aff' : '#ff6aae' }}>
    {label}
  </div>
);

export const OperatingModel = () => (
  <section id="model" data-college-section="model" data-testid="college-model"
    className="relative py-20 sm:py-28" style={{ background: 'linear-gradient(180deg, #050505, #0d0416 55%, #050505)' }}>
    <div className="px-6 sm:px-10 lg:px-24 max-w-[1400px] mx-auto">
      <Reveal>
        <Eyebrow>One Creative Hub. Multiple Opportunities.</Eyebrow>
        <SectionTitle>A Building That Works Beyond the College Day.</SectionTitle>
        <p className="text-white/65 max-w-3xl leading-relaxed">
          The UPA Creative Hub is designed as a vibrant, multi-use performing arts centre, maximising the use of
          its facilities throughout the week.
        </p>
      </Reveal>

      <div className="grid sm:grid-cols-3 gap-5 mt-12">
        {MODEL_BLOCKS.map((b, i) => (
          <Reveal key={b.when} delay={i * 120}>
            <Glass className="h-full">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-4 h-4" style={{ color: b.color }} />
                <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: b.color }}>{b.when}</p>
              </div>
              <h3 className="text-white font-heading text-lg mb-3">{b.what}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{b.body}</p>
            </Glass>
          </Reveal>
        ))}
      </div>

      <Reveal>
        <div className="mt-12 rounded-2xl border border-white/10 p-5 sm:p-8"
          style={{ background: 'linear-gradient(145deg, rgba(20,18,28,0.85), rgba(10,9,14,0.9))' }}
          data-testid="college-weekly-schedule" role="img" aria-label="Weekly operating schedule: weekday daytimes for proposed college education, evenings and weekends for UPA commercial programmes, school holidays for creative experiences">
          <h3 className="font-heading text-lg text-white mb-6">The week at a glance</h3>
          <div className="overflow-x-auto pb-1">
          <div className="min-w-[580px]">
          <div className="grid gap-2" style={{ gridTemplateColumns: 'minmax(58px,auto) repeat(7, 1fr)' }}>
            <div />
            {DAYS.map(d => <p key={d} className="text-center text-white/45 text-[10px] sm:text-xs uppercase tracking-wider pb-1">{d}</p>)}
            <p className="text-white/50 text-[10px] sm:text-xs self-center pr-2">Daytime</p>
            {DAYS.map(d => <Cell key={`day-${d}`} color={d === 'Sat' || d === 'Sun' ? PINK : PURPLE} label={d === 'Sat' || d === 'Sun' ? 'UPA' : 'College'} />)}
            <p className="text-white/50 text-[10px] sm:text-xs self-center pr-2">Evening</p>
            {DAYS.map(d => <Cell key={`eve-${d}`} color={PINK} label="UPA" />)}
          </div>
          <div className="mt-3 grid gap-2" style={{ gridTemplateColumns: 'minmax(58px,auto) 1fr' }}>
            <p className="text-white/50 text-[10px] sm:text-xs self-center pr-2">Holidays</p>
            <Cell color={GOLD} label="Bootcamps · Masterclasses · Intensives · Productions" />
          </div>
          </div>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6 text-xs text-white/55">
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ background: PURPLE }} aria-hidden="true" />Proposed post-16 college provision</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ background: PINK }} aria-hidden="true" />UPA commercial &amp; community programmes</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ background: GOLD }} aria-hidden="true" />Holiday creative experiences</span>
          </div>
        </div>
        <p className="text-white/65 max-w-3xl leading-relaxed mt-8">
          This dual operating model is intended to support financial sustainability while expanding access to
          high-quality performing arts opportunities across Milton Keynes.
        </p>
      </Reveal>
    </div>
  </section>
);

const SUSTAIN = [
  ['Education & Training Opportunities', 'Developing recognised 16–19 education pathways, alongside specialist professional training, with opportunities to explore education funding and strategic partnerships.', PURPLE],
  ['Diverse Revenue Opportunities', 'A complementary mix of educational provision, performing arts classes, professional workshops, holiday programmes, productions, private training and potential studio hire.', PINK],
  ['Property & Investment Potential', 'An opportunity to explore strategic investment, property partnerships, council collaboration and commercial development to bring an ambitious creative destination to life.', GOLD],
  ['A Scalable Creative Destination', 'A long-term vision to expand facilities, training programmes, industry partnerships and creative opportunities, establishing Milton Keynes as a destination for performing arts excellence.', '#3ED598'],
];

export const Sustainability = () => (
  <SectionShell id="sustainability" data-testid="college-sustainability">
    <Reveal>
      <Eyebrow color={GOLD}>Built for Growth. Designed for Impact.</Eyebrow>
      <SectionTitle>A Visionary Creative Hub With Exceptional Long-Term Potential.</SectionTitle>
      <p className="text-white/65 max-w-3xl leading-relaxed mb-4">
        The UPA Creative Hub represents an exciting opportunity to establish a landmark performing arts and creative
        education destination in Milton Keynes.
      </p>
      <p className="text-white/65 max-w-3xl leading-relaxed mb-4">
        Bringing together accredited education pathways, professional industry training, commercial programmes and
        community engagement, the Hub is designed to create a dynamic, multi-purpose destination with significant
        potential for growth, collaboration and long-term sustainability.
      </p>
    </Reveal>

    <div className="grid sm:grid-cols-2 gap-5 mt-10">
      {SUSTAIN.map(([t, b, c], i) => (
        <Reveal key={t} delay={i * 110}>
          <Glass className="h-full hover:border-white/25 transition-colors">
            <div className="flex items-center gap-3 mb-4">
              <Landmark className="w-4 h-4 shrink-0" style={{ color: c }} />
              <h3 className="text-white font-heading text-lg">{t}</h3>
            </div>
            <p className="text-white/60 text-sm leading-relaxed">{b}</p>
          </Glass>
        </Reveal>
      ))}
    </div>

    <Reveal>
      <p className="text-white/70 max-w-3xl leading-relaxed mt-10 border-l-2 pl-4" style={{ borderColor: PINK }}>
        A compelling opportunity to combine educational impact, commercial growth and community benefit within one
        ambitious creative destination.
      </p>
      <Disclaimer>
        Educational accreditation, funding arrangements and investment opportunities remain subject to development and approval.
      </Disclaimer>
    </Reveal>
  </SectionShell>
);
